import { useState, useEffect } from "react";
import axios from "axios";
import { CheckCircle, Shield, Clock, Zap, Star, Users, Globe, ArrowRight, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";

/**
 * Social proof: Avatar stack + customer count + star rating
 */
export function SocialProofBar({ t }) {
  const [count, setCount] = useState(3874);

  useEffect(() => {
    // Load real count from backend
    axios.get(`${API}/customer-count`).then(r => {
      if (r.data?.count) setCount(r.data.count);
    }).catch(() => {});

    // Increment by 1 every 120 minutes (7200000ms)
    const iv = setInterval(() => setCount(c => c + 1), 7200000);
    return () => clearInterval(iv);
  }, []);

  return (
    <div className="flex flex-wrap items-center gap-6 py-6" data-testid="social-proof-bar">
      {/* Avatar stack */}
      <div className="flex items-center">
        <div className="flex -space-x-2">
          {[
            "bg-gradient-to-br from-red-500 to-orange-500",
            "bg-gradient-to-br from-blue-500 to-cyan-500",
            "bg-gradient-to-br from-emerald-500 to-green-500",
            "bg-gradient-to-br from-purple-500 to-pink-500",
            "bg-gradient-to-br from-amber-500 to-yellow-500",
          ].map((bg, i) => (
            <div key={i} className={`w-8 h-8 ${bg} border-2 border-[#0a0a0a] flex items-center justify-center text-white text-[10px] font-bold`}>
              {["MK", "SL", "JP", "AT", "LB"][i]}
            </div>
          ))}
          <div className="w-8 h-8 bg-zinc-800 border-2 border-[#0a0a0a] flex items-center justify-center text-[var(--color-text-muted)] text-[9px] font-bold">
            +{(count - 5).toLocaleString()}
          </div>
        </div>
        <span className="ml-3 text-sm text-[var(--color-text)] font-medium">
          <span className="text-white font-bold">{count.toLocaleString()}+</span> {t.sp_businesses || "businesses worldwide trust TopRankPulse"}
        </span>
      </div>

      {/* Star rating */}
      <div className="flex items-center gap-1.5">
        <div className="flex">
          {[1, 2, 3, 4, 5].map(i => (
            <Star key={i} className="w-4 h-4 text-[#FFE600] fill-[#FFE600]" />
          ))}
        </div>
        <span className="text-sm text-[var(--color-text)] font-['JetBrains_Mono']">4.9/5</span>
      </div>
    </div>
  );
}

/**
 * Trust badges row — GDPR, SSL, No CC, 30s results
 */
export function TrustBadges({ t }) {
  const badges = [
    { icon: Shield, label: t.tb_gdpr || "GDPR Compliant", color: "text-emerald-400" },
    { icon: Shield, label: t.tb_ssl || "256-bit SSL", color: "text-blue-400" },
    { icon: CheckCircle, label: t.tb_no_cc || "No Credit Card", color: "text-amber-400" },
    { icon: Zap, label: t.tb_30s || "Results in 30s", color: "text-[#a855f7]" },
    { icon: Globe, label: t.tb_langs || "26 Languages", color: "text-purple-400" },
  ];

  return (
    <div className="flex flex-wrap gap-3 mt-4" data-testid="trust-badges">
      {badges.map((b, i) => (
        <div key={i} className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--color-surface)]/50 border border-[var(--color-border)]">
          <b.icon className={`w-3.5 h-3.5 ${b.color}`} />
          <span className="text-[11px] text-[var(--color-text-muted)] font-medium">{b.label}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * How it works — 3-step visual
 */
export function HowItWorks({ t }) {
  const steps = [
    { num: "01", title: t.hw_s1_t || "Enter your URL", desc: t.hw_s1_d || "Paste your website URL into the scanner. No signup required.", color: "text-[#a855f7]", border: "border-purple-900/30" },
    { num: "02", title: t.hw_s2_t || "AI analyzes everything", desc: t.hw_s2_d || "Our AI checks 50+ SEO factors, AI visibility across 4 engines, and GEO optimization.", color: "text-[#FFE600]", border: "border-yellow-900/30" },
    { num: "03", title: t.hw_s3_t || "Get actionable results", desc: t.hw_s3_d || "Receive your score, detailed report, and step-by-step fixes in 30 seconds. Free.", color: "text-emerald-400", border: "border-emerald-900/30" },
  ];

  return (
    <section className="py-20 px-6 bg-[var(--color-bg)] border-t border-[var(--color-border)]" data-testid="how-it-works">
      <div className="max-w-5xl mx-auto">
        <p className="text-[#a855f7] font-['JetBrains_Mono'] text-sm tracking-[0.3em] uppercase text-center mb-4">
          {t.hw_tag || "How it works"}
        </p>
        <h2 className="text-3xl sm:text-4xl font-black font-['Cabinet_Grotesk'] tracking-tighter text-center mb-12">
          {t.hw_title || "From zero to insights in"} <span className="text-[#a855f7]">{t.hw_seconds || "30 seconds"}</span>
        </h2>

        <div className="grid md:grid-cols-3 gap-0">
          {steps.map((step, i) => (
            <div key={i} className={`relative p-8 bg-[var(--color-surface)] border ${step.border}`} data-testid={`hw-step-${i}`}>
              {i < 2 && <ArrowRight className="hidden md:block absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 text-zinc-700 z-10" />}
              <div className={`text-5xl font-black font-['Cabinet_Grotesk'] ${step.color} opacity-20 mb-4`}>{step.num}</div>
              <h3 className="text-lg font-bold text-white font-['Cabinet_Grotesk'] mb-2">{step.title}</h3>
              <p className="text-sm text-[var(--color-text-muted)] leading-relaxed">{step.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * Floating CTA bar — appears on scroll. Closable; respects a 7-day
 * dismissal flag (localStorage) so closing it actually sticks across
 * tabs and days.
 */
const FLOATING_CTA_HIDE_KEY = "trp_floating_cta_hidden_at";
const FLOATING_CTA_HIDE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function _ctaHiddenRecently() {
  try {
    const ts = Number(localStorage.getItem(FLOATING_CTA_HIDE_KEY));
    if (!ts) return false;
    return Date.now() - ts < FLOATING_CTA_HIDE_MS;
  } catch {
    return false;
  }
}

export function FloatingCTA({ t, visible }) {
  const [closed, setClosed] = useState(() => _ctaHiddenRecently());
  if (!visible || closed) return null;

  const handleClose = () => {
    setClosed(true);
    try { localStorage.setItem(FLOATING_CTA_HIDE_KEY, String(Date.now())); } catch { /* ignore */ }
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-[#a855f7] border-t border-purple-400 px-4 py-3 shadow-2xl shadow-purple-900/50 transform transition-transform duration-300" data-testid="floating-cta" style={{ transform: visible ? 'translateY(0)' : 'translateY(100%)' }}>
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Zap className="w-5 h-5 text-white shrink-0" />
          <span className="text-white font-bold text-sm hidden sm:inline">
            {t.fcta_text || "Your competitors are ranking while you read this."}
          </span>
          <span className="text-white/70 text-xs hidden md:inline">
            {t.fcta_sub || "Free scan. No credit card. 30 seconds."}
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <a href="#hero-section" onClick={(e) => { e.preventDefault(); const el = document.querySelector('[data-testid="url-input"]'); if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); setTimeout(() => el.focus(), 500); } }} className="px-4 sm:px-6 py-2 bg-white text-[#a855f7] font-black text-sm uppercase tracking-wider hover:bg-zinc-100 transition-colors">
            {t.fcta_btn || "SCAN NOW"}
          </a>
          <button
            type="button"
            onClick={handleClose}
            aria-label={t.close || "Close"}
            data-testid="floating-cta-close"
            className="w-8 h-8 flex items-center justify-center text-white/80 hover:text-white hover:bg-white/15 rounded-full transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
